import { Component, OnInit, OnChanges, SimpleChanges, Input, inject, Optional, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { ManageChallengesSidebarComponent } from '../manage-challenges-sidebar/manage-challenges-sidebar.component';
import { ChallengeCardData } from '../challenge-card/challenge-card.component';
import { computeChallengeDaysRemaining, getActiveChallengeDeadlineEnd } from '../challenge-deadline.util';
import { getZeroPrizeDistribution, isNoCashPrizeType, normalizePrizeType } from '../challenge-prize.util';
import { ModalComponent } from '../../../shared/components/primeng';
import { ToasterService } from '../../../services/toaster.service';
import { ChallengeApiService, CatalogueApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { ChallengeActionButtonComponent } from './challenge-action-button/challenge-action-button.component';
import { ChallengeTimelineComponent, TimelineDates } from './challenge-timeline/challenge-timeline.component';
import { PrizePoolComponent, PrizePoolData } from './prize-pool/prize-pool.component';
import { ChallengeStatsComponent } from './challenge-stats/challenge-stats.component';
import { ChallengeTabsComponent, DetailTab } from './challenge-tabs/challenge-tabs.component';
import { ChallengeSectionsComponent, DetailSection } from './challenge-sections/challenge-sections.component';
import { ChallengeRulesContentComponent } from './challenge-rules-content/challenge-rules-content.component';
import { ChallengeAssetsContentComponent } from './challenge-assets-content/challenge-assets-content.component';
import { AssetCardData } from '../../../shared/components/asset-card/asset-card.component';
import { FormatService } from '../../../shared/services/format.service';
import { HelperService } from '../../../shared/services/helper.service';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { sanitizeRichHtmlForDisplay } from '../../../platform-admin/challenge/challenge-rich-text.util';

@Component({
  selector: 'app-challenge-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    ManageChallengesSidebarComponent,
    ModalComponent,
    LoginModalComponent,
    ChallengeActionButtonComponent,
    ChallengeTimelineComponent,
    PrizePoolComponent,
    ChallengeStatsComponent,
    ChallengeTabsComponent,
    ChallengeSectionsComponent,
    ChallengeRulesContentComponent,
    ChallengeAssetsContentComponent,
    PdfViewerModule
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class ChallengeDetailComponent implements OnInit, OnChanges {
  // Preview mode inputs (optional - when used in challenge creation flow)
  @Input() previewMode: boolean = false;
  @Input() previewTitle: string | null = null;
  @Input() previewSubtitle: string | null = null;
  @Input() previewTimelineDates: TimelineDates | null = null;
  @Input() previewPrizePool: PrizePoolData | null = null;
  @Input() previewTopSections: { objective: { label: string; content: string }; about: { label: string; content: string } } | null = null;
  @Input() previewChallengeImage: string | null | undefined = null;
  @Input() previewDetailSections: DetailSection[] | null = null;
  @Input() previewDatasets: AssetCardData[] | null = null;
  @Input() previewModels: AssetCardData[] | null = null;

  challengeId: string | null = null;
  challenge: ChallengeCardData | null = null;
  activeTab: string = ''; // Default tab for sidebar - empty to keep All Challenges collapsed
  activeDetailTab: DetailTab = 'details';
  isSidebarOpen: boolean = false; // Mobile sidebar state
  expandedSections: Set<string> = new Set(['constraints']); // Track which sections are expanded - default: constraints open
  showJoinModal: boolean = false; // Modal visibility for join competition
  showLoginModal: boolean = false; // Login modal when user clicks Join Challenge but is not logged in
  hasJoinedCompetition: boolean = false; // Track if user has joined the competition
  hasSubmittedSolution: boolean = false; // Track if user has submitted a solution
  /** When true (from API is_drafted), show "Resubmit Solution" instead of "Submit Solution". */
  isDrafted: boolean = false;
  showSubmitModal: boolean = false; // Modal visibility for submit solution
  showRulesPreviewModal: boolean = false; // Modal visibility for rules PDF preview
  loadingRulesPreview: boolean = false; // Loading URL before opening preview modal
  private toasterService = inject(ToasterService);
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private challengeApiService = inject(ChallengeApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private formatService = inject(FormatService);
  private helperService = inject(HelperService);
  private keycloak = inject(KeycloakService);
  loadingChallenge: boolean = false;
  loadingDatasetsAndModels: boolean = false;

  /** Header actions (Bookmark, Pin) - visibility and login from keycloak; Like hidden on challenge detail */
  isLoggedIn: boolean = false;
  isBookmarked: boolean = false;
  isPinned: boolean = false;
  
  // Submit Solution Form Data
  solutionName: string = '';
  solutionDescription: string = '';
  solutionZipFile: File | null = null;
  solutionInstructionsFile: File | null = null;
  /** Batch ID for attachment uploads in this submission; set when submit modal opens. */
  submissionBatchId: string | null = null;
  /** object_key from upload response; used to delete attachment before submit. */
  solutionZipObjectKey: string | null = null;
  solutionInstructionsObjectKey: string | null = null;
  uploadingAttachment: boolean = false;
  /** Loading previous submission when opening modal for resubmit. */
  loadingPreviousSubmission: boolean = false;
  /** Attachments from previous submission: filename -> { s3_key, metadata, uploaded_at }. Used for resubmit modal. */
  previousSubmissionAttachments: Record<string, { s3_key: string; metadata?: any; uploaded_at?: string }> = {};
  /** Snapshot when opening resubmit modal; used to disable Submit when nothing changed. */
  private initialSubmitFormState: {
    submissionId: string;
    name: string;
    description: string;
    attachmentKeys: string[];
    attachmentS3ByFilename: Record<string, string>;
  } | null = null;

  // Top sections above tabs (Objective and About)
  topSections = {
    objective: {
      label: 'Objective',
      content: 'Develop a robust machine learning model to predict crop yields based on historical agricultural data, weather patterns, and soil conditions. The solution should help farmers make informed decisions about crop selection, planting schedules, and resource allocation to maximize yield and minimize risk.'
    },
    about: {
      label: 'About the challenge',
      content: 'This challenge brings together data scientists, agricultural experts, and machine learning enthusiasts to tackle one of the most critical problems in modern agriculture. Participants will work with comprehensive datasets including historical crop yields, meteorological data, soil composition, and farming practices to create predictive models that can transform agricultural planning and decision-making.This challenge brings together data scientists, agricultural experts, and machine learning enthusiasts to tackle one of the most critical problems in modern agriculture. Participants will work with comprehensive datasets including historical crop yields, meteorological data, soil composition, and farming practices to create predictive models that can transform agricultural planning and decision-making.This challenge brings together data scientists, agricultural experts, and machine learning enthusiasts to tackle one of the most critical problems in modern agriculture. Participants will work with comprehensive datasets including historical crop yields, meteorological data, soil composition, and farming practices to create predictive models that can transform agricultural planning and decision-making.'
    }
  };
  
  challengeImage: string = 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=1200&h=400&fit=crop'; // Placeholder image

  /** Image natural width, set on load. If > 1000px use object-fit: contain. */
  detailChallengeImageWidth: number | null = null;

  get useDetailImageContain(): boolean {
    return this.detailChallengeImageWidth != null && this.detailChallengeImageWidth > 1000;
  }

  onDetailChallengeImageLoad(e: Event): void {
    const img = e.target as HTMLImageElement;
    if (img?.naturalWidth != null) {
      this.detailChallengeImageWidth = img.naturalWidth;
    }
  }
  
  // Challenge stats (host badge always "MahaAgX Team")
  challengeHost: string = 'MahaAgX Team';
  participantsCount: number = 0;
  submissionsCount: number = 0;
  challengeStartDate: Date | null = null;
  
  // Prize Pool data
  prizePool: PrizePoolData = {
    totalAmount: 0,
    paymentMethod: 'cash',
    distribution: null
  };
  
  // Timeline dates (from API timelines object)
  timelineDates: TimelineDates = {
    start: null,
    submissionStart: null,
    submissionEnd: null,
    evaluationEnd: null
  };

  /** Raw timelines from last API response (id, submission_starts_at, submission_ends_at, evaluation_ends_at). */
  private lastTimelines: Record<string, unknown> = {};

  /** Raw prize_pools from last API response (id, prize_type, total_pool_amount, currency, prize_description). */
  private lastPrizePools: Record<string, unknown> | null = null;

  /** Objective text from API detailed_description. */
  private lastDetailedDescription: string | null = null;

  /** Additional details tab: from API constraints, evaluation_criteria, other_resources. */
  private lastConstraints: string | null = null;
  private lastEvaluationCriteria: string | null = null;
  private lastOtherResources: string | null = null;

  /** Dataset and model refs from challenge API (datasets key): used to load cards via Items API when Data Banks tab is opened. */
  private lastChallengeDatasetsRefs: { id: string; name?: string }[] = [];
  private lastChallengeModelsRefs: { id: string; name?: string }[] = [];

  /** Map API prize_type to display payment method. */
  private mapPrizeTypeToPaymentMethod(prizeType: string | undefined): string {
    if (!prizeType) return 'cash';
    const t = normalizePrizeType(prizeType);
    if (t === 'NO_CASH') return 'No cash';
    if (t === 'CASH' || t === 'CHEQUE' || t === 'CASH_OR_CHEQUE') return 'Cash';
    return prizeType;
  }
  
  // Sections in tabs (excluding Objective and About)
  detailSections: DetailSection[] = [
    { 
      id: 'constraints', 
      label: 'Constraints', 
      icon: 'link',
      content: 'Participants must adhere to the following constraints:\n\n• Submissions must be in Python 3.8 or later\n• Maximum submission file size: 100MB\n• No external API calls allowed during evaluation\n• All models must be self-contained\n• Training data usage limited to provided datasets\n• No pre-trained models from external sources'
    },
    { 
      id: 'evaluation', 
      label: 'Evaluation', 
      icon: 'link',
      content: 'Submissions will be evaluated based on:\n\n• Accuracy: Primary metric (80% weight)\n• Efficiency: Model training and inference time (10% weight)\n• Code Quality: Readability and documentation (10% weight)\n\nEvaluation will be performed on a held-out test set. Final rankings will be determined by the combined weighted score.'
    },
    { 
      id: 'resources', 
      label: 'Other Resources', 
      icon: 'link',
      content: 'Additional resources available for participants:\n\n• Training datasets: Available in the Data Banks section\n• Documentation: Complete API reference and guides\n• Discussion Forum: Connect with other participants\n• Sample Code: Starter templates and examples\n• Webinars: Live sessions with challenge organizers'
    }
  ];

  /** Default content for Additional details tab; used when API does not provide constraints / evaluation_criteria / other_resources. */
  private getDefaultDetailSections(): DetailSection[] {
    return [
      { id: 'constraints', label: 'Constraints', icon: 'link', content: 'Participants must adhere to the following constraints:\n\n• Submissions must be in Python 3.8 or later\n• Maximum submission file size: 100MB\n• No external API calls allowed during evaluation\n• All models must be self-contained\n• Training data usage limited to provided datasets\n• No pre-trained models from external sources' },
      { id: 'evaluation', label: 'Evaluation', icon: 'link', content: 'Submissions will be evaluated based on:\n\n• Accuracy: Primary metric (80% weight)\n• Efficiency: Model training and inference time (10% weight)\n• Code Quality: Readability and documentation (10% weight)\n\nEvaluation will be performed on a held-out test set. Final rankings will be determined by the combined weighted score.' },
      { id: 'resources', label: 'Other Resources', icon: 'link', content: 'Additional resources available for participants:\n\n• Training datasets: Available in the Data Banks section\n• Documentation: Complete API reference and guides\n• Discussion Forum: Connect with other participants\n• Sample Code: Starter templates and examples\n• Webinars: Live sessions with challenge organizers' }
    ];
  }

  // Rules and Guidelines PDF URL (static fallback)
  rulesPdfUrl: string = '/assets/documents/challenge-rules-and-guidelines.pdf';
  /** URL for embedded preview (from API when Rules tab is selected); null until loaded or fallback. */
  rulesPreviewUrl: string | null = null;
  /** URL used in the rules preview modal (blob URL so PDF displays in viewer). */
  rulesPreviewModalUrl: string | null = null;
  /** True while fetching PDF content after opening the modal. */
  rulesPreviewPdfLoading: boolean = false;
  private rulesPreviewModalBlobUrl: string | null = null;

  // Datasets and Models
  datasets: AssetCardData[] = [];
  models: AssetCardData[] = [];

  get headerConfig(): AssetHeaderConfig {
    // In preview mode, use preview data if available
    if (this.previewMode && this.previewTopSections) {
      return {
        breadcrumbs: [],
        title: (this.previewTitle?.trim() || 'Challenge Preview'),
        description: (this.previewSubtitle?.trim() || this.previewTopSections.about?.content || '')
      };
    }
    
    // Normal mode - use challenge data
    return {
      breadcrumbs: [
        { label: 'Home', route: '/' },
        { label: 'Challenges', route: '/challenges' },
        { label: this.challenge?.name || 'Challenge Details' }
      ],
      title: this.challenge?.name || 'Challenge Details',
      description: (this.challenge?.provider ?? this.challenge?.description) || 'View detailed information about this challenge including requirements, timeline, and submission guidelines.',
      assetId: this.challengeId ?? undefined
    };
  }

  constructor(
    @Optional() private route: ActivatedRoute,
    @Optional() private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When preview inputs change (e.g. modal opened with current form data), refresh preview data
    if (this.previewMode && (changes['previewTopSections'] || changes['previewTimelineDates'] || changes['previewPrizePool'] || changes['previewChallengeImage'] || changes['previewDetailSections'] || changes['previewDatasets'] || changes['previewModels'] || changes['previewTitle'] || changes['previewSubtitle'])) {
      this.loadPreviewData();
    }
  }

  ngOnInit(): void {
    // Set Additional Details tab as default on page load
    this.activeDetailTab = 'details';
    
    // If in preview mode, populate from preview inputs
    if (this.previewMode) {
      this.loadPreviewData();
      return;
    }
    
    // Normal mode - load from route params
    if (this.route) {
    this.route.paramMap.subscribe(params => {
      this.challengeId = params.get('id');
      this.loadChallengeDetails();
    });
    
    // Optionally, check for query parameter to override tab, but default to 'details'
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['tab'] && ['details', 'rules', 'data-banks'].includes(queryParams['tab'])) {
        this.activeDetailTab = queryParams['tab'] as DetailTab;
      } else {
        // Default to 'details' if no valid tab query param
        this.activeDetailTab = 'details';
      }
    });
    }
  }

  loadPreviewData(): void {
    // Populate timeline dates from preview input or use dummy data
    if (this.previewTimelineDates && this.previewTimelineDates.start) {
      this.timelineDates = this.previewTimelineDates;
    } else {
      // Dummy timeline data
      const today = new Date();
      this.timelineDates = {
        start: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        submissionStart: new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
        submissionEnd: new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000), // 60 days from now
        evaluationEnd: new Date(today.getTime() + 75 * 24 * 60 * 60 * 1000) // 75 days from now
      };
    }
    
    // Populate prize pool from preview input or use dummy data
    if (this.previewPrizePool && this.previewPrizePool.totalAmount > 0) {
      this.prizePool = this.previewPrizePool;
    } else {
      // Dummy prize pool data
      this.prizePool = {
        totalAmount: 250000,
        paymentMethod: 'cash',
        distribution: [
          { position: '1st', amount: 125000 },
          { position: '2nd', amount: 75000 },
          { position: '3rd', amount: 50000 }
        ]
      };
    }
    
    // Populate top sections from preview input (use form data whenever provided, even if empty); strip HTML/scripts
    if (this.previewTopSections) {
      this.topSections = {
        objective: {
          label: this.previewTopSections.objective?.label ?? 'Objective',
          content: sanitizeRichHtmlForDisplay(this.previewTopSections.objective?.content ?? '')
        },
        about: {
          label: this.previewTopSections.about?.label ?? 'About the challenge',
          content: sanitizeRichHtmlForDisplay(this.previewTopSections.about?.content ?? '')
        }
      };
    } else {
      // Fallback only when preview inputs are not provided (e.g. non-preview usage)
      this.topSections = {
        objective: {
          label: 'Objective',
          content: 'Develop a robust machine learning model to predict crop yields based on historical agricultural data, weather patterns, and soil conditions. The solution should help farmers make informed decisions about crop selection, planting schedules, and resource allocation to maximize yield and minimize risk.'
        },
        about: {
          label: 'About the challenge',
          content: 'This challenge brings together data scientists, agricultural experts, and machine learning enthusiasts to tackle one of the most critical problems in modern agriculture. Participants will work with comprehensive datasets including historical crop yields, meteorological data, soil composition, and farming practices to create predictive models that can transform agricultural planning and decision-making.'
        }
      };
    }
    
    // Populate challenge image from preview input or use dummy image
    if (this.previewChallengeImage) {
      this.challengeImage = this.previewChallengeImage ?? '';
      this.detailChallengeImageWidth = null;
    } else {
      // Dummy image URL
      this.challengeImage = 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=1200&h=400&fit=crop';
      this.detailChallengeImageWidth = null;
    }
    
    // Populate detail sections from preview input or use dummy data; strip HTML/scripts from content
    if (this.previewDetailSections && this.previewDetailSections.length > 0) {
      this.detailSections = this.previewDetailSections.map(s => ({
        ...s,
        content: sanitizeRichHtmlForDisplay(s.content ?? '')
      }));
    } else {
      // Dummy detail sections data
      this.detailSections = [
        {
          id: 'constraints',
          label: 'Constraints',
          icon: 'list',
          content: 'Participants must adhere to the following constraints:\n\n• Submissions must be in Python 3.8 or later\n• Maximum submission file size: 100MB\n• No external API calls allowed during evaluation\n• All models must be self-contained\n• Training data usage limited to provided datasets\n• No pre-trained models from external sources'
        },
        {
          id: 'evaluation',
          label: 'Evaluation',
          icon: 'check-circle',
          content: 'Submissions will be evaluated based on:\n\n• Accuracy: Primary metric (80% weight)\n• Efficiency: Model training and inference time (10% weight)\n• Code Quality: Readability and documentation (10% weight)\n\nEvaluation will be performed on a held-out test set. Final rankings will be determined by the combined weighted score.'
        },
        {
          id: 'additional-info',
          label: 'Additional Information',
          icon: 'info',
          content: 'Additional resources and information for participants:\n\n• Training datasets: Available in the Data Banks section\n• Documentation: Complete API reference and guides\n• Discussion Forum: Connect with other participants\n• Sample Code: Starter templates and examples\n• Webinars: Live sessions with challenge organizers'
        }
      ];
    }
    
    // Populate datasets and models from preview input or use dummy data
    if (this.previewDatasets && this.previewDatasets.length > 0) {
      this.datasets = this.previewDatasets;
    } else {
      // Dummy datasets data
      this.datasets = [
        {
          id: 'dummy-1',
          title: 'Agricultural Yield Dataset 2024',
          provider: 'Department of Agriculture',
          description: 'Comprehensive dataset containing crop yields, weather data, and soil composition metrics for the year 2024.',
          metadata: {
            type: 'dataset',
            format: 'CSV',
            date: '2024-01-15'
          },
          badge: {
            label: 'Open',
            color: 'public'
          }
        },
        {
          id: 'dummy-2',
          title: 'Weather Pattern Dataset',
          provider: 'IMD Research Division',
          description: 'Historical weather patterns and climate data for agricultural regions.',
          metadata: {
            type: 'dataset',
            format: 'CSV',
            date: '2024-02-01'
          },
          badge: {
            label: 'Open',
            color: 'public'
          }
        }
      ];
    }
    
    if (this.previewModels && this.previewModels.length > 0) {
      this.models = this.previewModels;
    } else {
      // Dummy models data
      this.models = [
        {
          id: 'dummy-1',
          title: 'Crop Yield Prediction Model v2.1',
          provider: 'AgriTech Solutions',
          description: 'Advanced machine learning model for predicting crop yields based on historical data and weather patterns.',
          metadata: {
            type: 'model',
            format: 'TensorFlow',
            date: '2024-01-20'
          },
          badge: {
            label: 'Open',
            color: 'public'
          }
        }
      ];
    }
    
    // Set dummy challenge stats
    this.participantsCount = 0;
    this.challengeHost = 'MahaAgX';
    this.submissionsCount = 0;
    this.challengeStartDate = this.timelineDates.start;
  }

  /** Map API by_id response to ChallengeCardData (same shape as home list API). */
  private mapApiChallengeToCardData(item: any): ChallengeCardData {
    const id = item?.id ?? item?._id ?? this.challengeId ?? '';
    const name = item?.title ?? item?.name ?? item?.challengeName ?? '';
    const description = item?.description ?? item?.short_description ?? '';
    const provider = item?.subtitle ?? item?.organization ?? item?.provider ?? item?.organizer ?? 'MahaAgX Team';
    const imageUrl = item?.image_url ?? item?.imageUrl ?? item?.banner_image ?? undefined;
    const participants = item?.participant_count ?? item?.interested_participants ?? item?.participants ?? 0;
    const submissions = item?.submission_count ?? 0;
    const prizePools = item?.prize_pools;
    const rawPrizeMoney = prizePools?.total_pool_amount ?? item?.prize_money ?? item?.prizeMoney ?? item?.prize ?? 0;
    const prizeMoney = isNoCashPrizeType(prizePools?.prize_type ?? item?.prize_type) ? 0 : rawPrizeMoney;
    const currency = prizePools?.currency ?? item?.currency ?? undefined;
    const timelines = item?.timelines ?? {};
    const submissionStartsAt = timelines?.submission_starts_at ?? item?.submission_starts_at ?? undefined;
    const submissionEndsAt = timelines?.submission_ends_at ?? item?.submission_ends_at ?? item?.end_date ?? undefined;
    const evaluationEndsAt = timelines?.evaluation_ends_at ?? item?.evaluation_ends_at ?? undefined;
    const deadlineInput = {
      submissionEndsAt: submissionEndsAt || undefined,
      evaluationEndsAt: evaluationEndsAt || undefined,
    };
    const activeEndDate = getActiveChallengeDeadlineEnd(deadlineInput);
    const daysRemaining = activeEndDate ? Math.max(0, computeChallengeDaysRemaining(activeEndDate)) : 0;
    return {
      id,
      name,
      description,
      provider,
      imageUrl,
      participants,
      submissions,
      prizeMoney,
      currency,
      daysRemaining,
      endDate: activeEndDate || undefined,
      submissionStartsAt: submissionStartsAt || undefined,
      submissionEndsAt: submissionEndsAt || undefined,
      evaluationEndsAt: evaluationEndsAt || undefined,
      link: `/challenges/${id}`
    };
  }

  async loadChallengeDetails(): Promise<void> {
    if (!this.challengeId) return;

    this.loadingChallenge = true;
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      const raw = await this.challengeApiService.getChallengeById(this.challengeId);
      const item = raw?.data ?? raw?.challenge ?? raw;
      if (item && (item.id || item._id || item.title || item.name)) {
        this.challenge = this.mapApiChallengeToCardData(item);
        const rawTimelines = item?.timelines && typeof item.timelines === 'object' ? (item.timelines as Record<string, unknown>) : {};
        const itemAny = item as Record<string, unknown>;
        this.lastTimelines = {
          ...rawTimelines,
          submission_starts_at: rawTimelines['submission_starts_at'] ?? item?.submission_starts_at,
          submission_ends_at: rawTimelines['submission_ends_at'] ?? item?.submission_ends_at,
          evaluation_ends_at: rawTimelines['evaluation_ends_at'] ?? item?.evaluation_ends_at,
          published_at: rawTimelines['published_at'] ?? itemAny?.['published_at']
        };
        const rawPrize = item?.prize_pools && typeof item.prize_pools === 'object' ? (item.prize_pools as Record<string, unknown>) : null;
        this.lastPrizePools = rawPrize ? { ...rawPrize } : null;
        this.lastDetailedDescription = typeof item?.detailed_description === 'string' ? sanitizeRichHtmlForDisplay(item.detailed_description) : null;
        this.lastConstraints = typeof item?.constraints === 'string' ? sanitizeRichHtmlForDisplay(item.constraints) : null;
        const evalCriteria =
          item?.evaluation_criteria_definition ??
          item?.evaluations?.evaluation_criteria ??
          item?.evaluation_criteria;
        this.lastEvaluationCriteria = typeof evalCriteria === 'string' ? sanitizeRichHtmlForDisplay(evalCriteria) : null;
        const otherResources = item?.additional_information ?? item?.other_resources;
        this.lastOtherResources = typeof otherResources === 'string' ? sanitizeRichHtmlForDisplay(otherResources) : null;
        // Datasets key: object with datasets[] and ai_models[] (asset IDs for Items API), or top-level arrays
        const dsPayload = item?.datasets;
        if (dsPayload && typeof dsPayload === 'object') {
          if (Array.isArray(dsPayload.datasets) || Array.isArray(dsPayload.ai_models)) {
            this.lastChallengeDatasetsRefs = Array.isArray(dsPayload.datasets) ? dsPayload.datasets : [];
            this.lastChallengeModelsRefs = Array.isArray(dsPayload.ai_models) ? dsPayload.ai_models : [];
          } else if (Array.isArray(dsPayload)) {
            this.lastChallengeDatasetsRefs = dsPayload;
            this.lastChallengeModelsRefs = Array.isArray(item?.ai_models) ? item.ai_models : [];
          } else {
            this.lastChallengeDatasetsRefs = [];
            this.lastChallengeModelsRefs = [];
          }
        } else {
          this.lastChallengeDatasetsRefs = [];
          this.lastChallengeModelsRefs = [];
        }
        this.hasJoinedCompetition = !!(item as Record<string, unknown>)?.['is_joined'];
        this.isDrafted = !!((item as Record<string, unknown>)?.['is_drafted'] ?? (raw as Record<string, unknown>)?.['is_drafted']);
        this.isBookmarked = !!((item as Record<string, unknown>)?.['is_bookmarked'] ?? item?.bookmarked ?? false);
      } else {
        this.fallbackToSampleChallenge();
        return;
      }
    } catch {
      this.fallbackToSampleChallenge();
      return;
    } finally {
      this.loadingChallenge = false;
    }

    this.applyChallengeToDetail();

    // If navigated with ?resubmit=1 (e.g. from Challenges Submitted > Resubmission), open the resubmission modal
    if (this.route?.snapshot?.queryParamMap?.get('resubmit') === '1') {
      this.onSubmitSolution();
      // Clear resubmit from URL so refresh does not re-open the modal
      this.router?.navigate([], {
        relativeTo: this.route,
        queryParams: { resubmit: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  async toggleChallengeBookmark(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.challengeId) return;
    try {
      if (this.isBookmarked) {
        await this.challengeApiService.deleteBookmarkChallenge(this.challengeId);
        this.isBookmarked = false;
        this.toasterService.success('Bookmark removed', 3000, false);
      } else {
        await this.challengeApiService.bookmarkChallenge(this.challengeId);
        this.isBookmarked = true;
        this.toasterService.success('Challenge bookmarked', 3000, false);
      }
      this.cdr.markForCheck();
    } catch {
      this.toasterService.error(this.isBookmarked ? 'Failed to remove bookmark.' : 'Failed to bookmark challenge.', 3000);
      this.cdr.markForCheck();
    }
  }

  toggleChallengePin(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    // TODO: wire to challenge pin API when available
    this.isPinned = !this.isPinned;
    this.cdr.detectChanges();
  }

  /** Fallback when API fails or returns no data (e.g. demo IDs). */
  private fallbackToSampleChallenge(): void {
    const sampleChallenges: ChallengeCardData[] = [
      { id: '1', name: 'Agricultural Data Prediction Challenge', description: 'Predict crop yields and agricultural outcomes using advanced machine learning techniques.', provider: 'MahaAgX Team', participants: 37, submissions: 22, prizeMoney: 192113, daysRemaining: 15 },
      { id: '2', name: 'Crop Yield Forecasting Competition', description: 'Forecast crop yields based on historical data and weather patterns.', provider: 'AgriTech Solutions', participants: 45, submissions: 18, prizeMoney: 250000, daysRemaining: 12 },
      { id: '3', name: 'Weather Pattern Analysis Challenge', description: 'Analyze weather patterns and climate data to predict agricultural impacts.', provider: 'IMD Research Division', participants: 28, submissions: 14, prizeMoney: 180000, daysRemaining: 20 },
      { id: '4', name: 'Soil Quality Assessment Challenge', description: 'Evaluate soil health using various sensor data inputs.', provider: 'Department of Agriculture', participants: 32, submissions: 9, prizeMoney: 180000, daysRemaining: 8 },
      { id: '5', name: 'Irrigation Optimization Competition', description: 'Develop algorithms to optimize irrigation systems.', provider: 'Water Management Board', participants: 78, submissions: 45, prizeMoney: 345666, daysRemaining: 0 },
      { id: '6', name: 'Pest Detection ML Challenge', description: 'Build ML models to identify agricultural pests from images.', provider: 'AgriResearch Institute', participants: 92, submissions: 67, prizeMoney: 420000, daysRemaining: 0 },
      { id: 'eval-1', name: 'Crop Yield Forecasting Competition', description: 'Predict crop yields based on historical data.', provider: 'AgriTech Solutions', participants: 45, submissions: 18, prizeMoney: 250000, daysRemaining: 15 },
      { id: 'eval-2', name: 'Soil Quality Assessment Challenge', description: 'Evaluate soil health using sensor data.', provider: 'Department of Agriculture', participants: 32, submissions: 9, prizeMoney: 180000, daysRemaining: 8 },
      { id: 'past-1', name: 'Irrigation Optimization Competition', description: 'Optimize irrigation for water efficiency.', provider: 'Water Management Board', participants: 78, submissions: 45, prizeMoney: 345666, daysRemaining: 0 },
      { id: 'past-2', name: 'Pest Detection ML Challenge', description: 'Identify agricultural pests from images.', provider: 'AgriResearch Institute', participants: 92, submissions: 67, prizeMoney: 420000, daysRemaining: 0 }
    ];
    this.challenge = sampleChallenges.find(c => c.id === this.challengeId!) || null;
    this.lastTimelines = {};
    this.lastPrizePools = null;
    this.lastDetailedDescription = null;
    this.lastConstraints = null;
    this.lastEvaluationCriteria = null;
    this.lastOtherResources = null;
    this.lastChallengeDatasetsRefs = [];
    this.lastChallengeModelsRefs = [];
    this.applyChallengeToDetail();
  }

  /** Set timeline, prize pool, image, and stats from this.challenge. */
  private applyChallengeToDetail(): void {
    if (!this.challenge) return;

    this.participantsCount = this.challenge.participants ?? 0;
    this.challengeHost = 'MahaAgX Team';
    this.submissionsCount = this.challenge.submissions ?? 0;

    if (this.lastDetailedDescription != null && this.lastDetailedDescription !== '') {
      this.topSections = {
        ...this.topSections,
        objective: { ...this.topSections.objective, content: this.lastDetailedDescription },
        about: { ...this.topSections.about, content: this.lastDetailedDescription }
      };
    }
    const timelines = this.lastTimelines as Record<string, string | undefined>;
    const publishedAt = timelines?.['published_at'];
    const submissionStartsAt = timelines?.['submission_starts_at'] ?? this.challenge.submissionStartsAt ?? undefined;
    const submissionEndsAt = timelines?.['submission_ends_at'] ?? this.challenge.submissionEndsAt ?? undefined;
    const evaluationEndsAt = timelines?.['evaluation_ends_at'] ?? undefined;

    this.challengeStartDate = submissionStartsAt ? new Date(submissionStartsAt) : new Date('2024-01-15');

    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 30);
    // Timeline: Start = published_at from API (when available), others from submission/evaluation; all displayed in IST
    this.timelineDates = {
      start: publishedAt ? new Date(publishedAt) : (submissionStartsAt ? new Date(submissionStartsAt) : new Date('2024-12-01')),
      submissionStart: submissionStartsAt ? new Date(submissionStartsAt) : new Date('2024-12-15'),
      submissionEnd: submissionEndsAt ? new Date(submissionEndsAt) : futureDate,
      evaluationEnd: evaluationEndsAt ? new Date(evaluationEndsAt) : new Date('2025-02-15')
    };

    // Prize pool from API prize_pools (prize_type, total_pool_amount, currency, prize_description)
    const pool = this.lastPrizePools as Record<string, unknown> | null;
    const prizeType = (pool?.['prize_type'] as string | undefined) ?? undefined;
    const isNoCash = isNoCashPrizeType(prizeType);
    this.prizePool.paymentMethod = pool ? this.mapPrizeTypeToPaymentMethod(prizeType) : 'cash';

    if (isNoCash) {
      this.prizePool.totalAmount = 0;
      this.prizePool.distribution = getZeroPrizeDistribution();
    } else {
      const totalPoolAmount = pool?.['total_pool_amount'] != null
        ? Number(pool['total_pool_amount'])
        : (this.challenge.prizeMoney ?? 0);
      this.prizePool.totalAmount = totalPoolAmount;
      if (totalPoolAmount > 0) {
        this.prizePool.distribution = [
          { position: '1st', amount: Math.floor(totalPoolAmount * 0.5) },
          { position: '2nd', amount: Math.floor(totalPoolAmount * 0.3) },
          { position: '3rd', amount: Math.floor(totalPoolAmount * 0.2) }
        ];
      } else {
        this.prizePool.distribution = null;
      }
    }

    if (this.challenge.imageUrl) {
      this.challengeImage = this.challenge.imageUrl ?? '';
      this.detailChallengeImageWidth = null;
    }

    // Additional details tab: Constraints, Evaluation, Other Resources from API (constraints, evaluation_criteria, other_resources)
    const defaultSections = this.getDefaultDetailSections();
    this.detailSections = defaultSections.map(section => {
      if (section.id === 'constraints' && this.lastConstraints != null && this.lastConstraints !== '') {
        return { ...section, content: this.lastConstraints };
      }
      if (section.id === 'evaluation' && this.lastEvaluationCriteria != null && this.lastEvaluationCriteria !== '') {
        return { ...section, content: this.lastEvaluationCriteria };
      }
      if (section.id === 'resources' && this.lastOtherResources != null && this.lastOtherResources !== '') {
        return { ...section, content: this.lastOtherResources };
      }
      return section;
    });

    this.datasets = [];
    this.models = [];
    if (this.activeDetailTab === 'data-banks') {
      this.loadDatasetsAndModelsFromApi();
    }
  }

  /** Unwrap item from getItemById response (result / data or direct). */
  private unwrapItemResponse(response: any): any | null {
    if (!response) return null;
    if (response?.result !== undefined) {
      const r = response.result;
      return Array.isArray(r) ? (r.length > 0 ? r[0] : null) : r;
    }
    if (response?.data !== undefined) {
      const d = response.data;
      return Array.isArray(d) ? (d.length > 0 ? d[0] : null) : d;
    }
    return response;
  }

  /** Load datasets and models for Data Banks tab: use asset IDs from challenge API, then Item API (getItemById) for full details. */
  async loadDatasetsAndModelsFromApi(): Promise<void> {
    if (this.previewMode) return;
    this.datasets = [];
    this.models = [];
    if (this.lastChallengeDatasetsRefs.length === 0 && this.lastChallengeModelsRefs.length === 0) {
      return;
    }
    this.loadingDatasetsAndModels = true;
    try {
      const fetchDatasetItem = async (ref: { id: string; name?: string }): Promise<AssetCardData> => {
        try {
          const res = await this.catalogueApiService.getItemById(ref.id);
          const item = this.unwrapItemResponse(res);
          return item ? this.mapCatalogueItemToDatasetCard(item) : this.placeholderDatasetCard(ref);
        } catch {
          return this.placeholderDatasetCard(ref);
        }
      };
      const fetchModelItem = async (ref: { id: string; name?: string }): Promise<AssetCardData> => {
        try {
          const res = await this.catalogueApiService.getItemById(ref.id);
          const item = this.unwrapItemResponse(res);
          return item ? this.mapCatalogueItemToModelCard(item) : this.placeholderModelCard(ref);
        } catch {
          return this.placeholderModelCard(ref);
        }
      };

      const [datasetCards, modelCards] = await Promise.all([
        Promise.all(this.lastChallengeDatasetsRefs.map(fetchDatasetItem)),
        Promise.all(this.lastChallengeModelsRefs.map(fetchModelItem)),
      ]);
      this.datasets = datasetCards;
      this.models = modelCards;
    } catch {
      this.toasterService.error('Failed to load datasets and models.');
    } finally {
      this.loadingDatasetsAndModels = false;
    }
  }

  private extractItemsFromSearchResponse(res: any): any[] {
    if (!res) return [];
    const list = res?.result ?? res?.results ?? res?.data?.result ?? res?.data?.results ?? res?.data;
    return Array.isArray(list) ? list : [];
  }

  private isRecentItem(dateString: string | Date | null | undefined): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

  private mapCatalogueItemToDatasetCard(item: any): AssetCardData {
    const id = item?.id ?? '';
    const title = item?.label ?? item?.name ?? item?.title ?? item?.itemName ?? 'Untitled';
    const rawDesc = item?.shortDescription ?? item?.short_description ?? item?.description ?? '';
    const descStr = typeof rawDesc === 'string' ? rawDesc.replace(/<[^>]*>/g, '').trim() : '';
    const description = descStr.length > 120 ? descStr.slice(0, 120) + '…' : descStr || undefined;
    const format = item?.fileFormat ?? item?.format ?? '';
    const dateRaw = item?.itemCreatedAt ?? item?.createdAt ?? item?.created_at;
    const date = dateRaw ? this.formatService.formatDateForCard(dateRaw) : undefined;
    const provider = item?.provider ?? item?.organisation ?? item?.organization ?? item?.provider_name ?? '';
    const tags: string[] = Array.isArray(item?.tags) ? item.tags.slice(0, 5) : [];
    const itemWithAccessPolicy = { ...item, accessPolicy: item?.accessPolicy ?? item?.access_policy };
    const badgeLabel = this.helperService.extractAccessBadge(itemWithAccessPolicy);
    const badgeColor = this.helperService.getBadgeColorVariant(badgeLabel);
    const metrics = item?.metrics ?? {};
    const isNew = this.isRecentItem(dateRaw);
    return {
      id,
      title,
      provider: provider || undefined,
      organisation: provider || undefined,
      description,
      tags: tags.length > 0 ? tags : undefined,
      metadata: {
        type: 'dataset',
        format: format || undefined,
        date
      },
      badge: { label: badgeLabel, color: badgeColor },
      isNew,
      likes: metrics.likes ?? item?.likeCount ?? item?.likes ?? item?.like_count ?? 0,
      views: metrics.views ?? item?.viewCount ?? item?.views ?? item?.view_count ?? 0,
      downloads: metrics.downloads ?? item?.downloadCount ?? item?.downloads ?? item?.download_count ?? 0
    };
  }

  private mapCatalogueItemToModelCard(item: any): AssetCardData {
    const id = item?.id ?? '';
    const title = item?.label ?? item?.name ?? item?.title ?? item?.itemName ?? 'Untitled';
    const rawDesc = item?.shortDescription ?? item?.short_description ?? item?.description ?? '';
    const descStr = typeof rawDesc === 'string' ? rawDesc.replace(/<[^>]*>/g, '').trim() : '';
    const description = descStr.length > 120 ? descStr.slice(0, 120) + '…' : descStr || undefined;
    const modelType = item?.modelType ?? '';
    const percentage = item?.percentage ?? item?.dataReadiness ?? undefined;
    const dateRaw = item?.itemCreatedAt ?? item?.createdAt ?? item?.created_at;
    const date = dateRaw ? this.formatService.formatDateForModelCard(dateRaw) : undefined;
    const provider = item?.provider ?? item?.organisation ?? item?.organization ?? item?.provider_name ?? '';
    const tags: string[] = Array.isArray(item?.tags) ? item.tags.slice(0, 5) : [];
    const itemWithAccessPolicy = { ...item, accessPolicy: item?.accessPolicy ?? item?.access_policy };
    const badgeLabel = this.helperService.extractAccessBadge(itemWithAccessPolicy);
    const badgeColor = this.helperService.getBadgeColorVariant(badgeLabel);
    const metrics = item?.metrics ?? {};
    const isNew = this.isRecentItem(dateRaw);
    return {
      id,
      title,
      provider: provider || undefined,
      organisation: provider || undefined,
      description,
      tags: tags.length > 0 ? tags : undefined,
      metadata: {
        type: 'model',
        modelType: modelType || undefined,
        percentage,
        date
      },
      badge: { label: badgeLabel, color: badgeColor },
      isNew,
      likes: metrics.likes ?? item?.likeCount ?? item?.likes ?? item?.like_count ?? 0,
      views: metrics.views ?? item?.viewCount ?? item?.views ?? item?.view_count ?? 0,
      downloads: metrics.downloads ?? item?.downloadCount ?? item?.downloads ?? item?.download_count ?? 0
    };
  }

  private placeholderDatasetCard(ref: { id: string; name?: string }): AssetCardData {
    return {
      id: ref.id,
      title: ref.name ?? 'Dataset',
      metadata: { type: 'dataset' },
      badge: { label: 'Open', color: 'public' },
      likes: 0,
      views: 0,
      downloads: 0
    };
  }

  private placeholderModelCard(ref: { id: string; name?: string }): AssetCardData {
    return {
      id: ref.id,
      title: ref.name ?? 'Model',
      metadata: { type: 'model' },
      badge: { label: 'Open', color: 'public' },
      likes: 0,
      views: 0,
      downloads: 0
    };
  }

  setDetailTab(tab: DetailTab): void {
    this.activeDetailTab = tab;
  }

  onTabChange(tab: DetailTab): void {
    this.activeDetailTab = tab;
    if (tab === 'rules') {
      // Do not fetch or load PDF when tab is selected; only when user clicks Preview
    }
    if (tab === 'data-banks') {
      this.loadDatasetsAndModelsFromApi();
    }
  }

  private async loadRulesPreviewUrl(): Promise<void> {
    if (!this.challengeId) return;
    try {
      const res = await this.challengeApiService.downloadChallengeRulesAndGuideline(this.challengeId);
      this.rulesPreviewUrl = this.getRulesDownloadUrlFromResponse(res) || this.rulesPdfUrl || null;
    } catch {
      this.rulesPreviewUrl = this.rulesPdfUrl || null;
    }
  }

  onSectionToggle(sectionId: string): void {
    this.toggleSection(sectionId);
  }

  /** Ensure user is logged in before opening join modal; show login modal if not. */
  async onJoinChallengeClick(): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.cdr.markForCheck();
        return;
      }
      this.showJoinModal = true;
    } catch {
      this.showLoginModal = true;
      this.cdr.markForCheck();
    }
  }

  openJoinModal(): void {
    this.showJoinModal = true;
  }

  closeJoinModal(): void {
    this.showJoinModal = false;
  }

  async onJoinModalConfirm(): Promise<void> {
    if (!this.challengeId) {
      this.toasterService.error('Unable to join challenge.', 3000);
      return;
    }
    try {
      await this.challengeApiService.joinChallenge(this.challengeId);
      this.hasJoinedCompetition = true;
      this.showJoinModal = false;
      this.toasterService.success('Challenge joined successfully', 3000);
    } catch {
      this.toasterService.error('Failed to join challenge. Please try again.', 3000);
    }
  }

  onJoinModalCancel(): void {
    this.showJoinModal = false;
  }

  async onSubmitSolution(): Promise<void> {
    this.ensureSubmissionBatchId();
    // Fetch previous submission when user has joined (so we can pre-fill if they already submitted)
    if (this.challengeId) {
      this.loadingPreviousSubmission = true;
      this.previousSubmissionAttachments = {};
      this.cdr.markForCheck();
      try {
        const res = await this.challengeApiService.getUserSubmissions(this.challengeId, 1, 1);
        const list =
          Array.isArray(res) ? res
          : (res?.data?.submissions ?? res?.data ?? res?.submissions ?? []);
        const first = Array.isArray(list) ? list[0] : null;
        if (first && typeof first === 'object') {
          this.solutionName = first.title ?? this.solutionName;
          this.solutionDescription = first.description ?? this.solutionDescription;
          const att = first.attachments;
          if (att && typeof att === 'object' && !Array.isArray(att)) {
            const mapped: Record<string, { s3_key: string; metadata?: any; uploaded_at?: string }> = {};
            for (const [filename, val] of Object.entries(att)) {
              const v = val as any;
              const s3Key = v?.s3_key ?? v?.object_key ?? v?.key ?? '';
              if (s3Key) mapped[filename] = { s3_key: s3Key, metadata: v?.metadata, uploaded_at: v?.uploaded_at };
            }
            this.previousSubmissionAttachments = mapped;
            const s3ByFilename: Record<string, string> = {};
            for (const [fn, v] of Object.entries(mapped)) {
              if (v?.s3_key) s3ByFilename[fn] = v.s3_key;
            }
            const submissionId = (first as any)?.id ?? (first as any)?.submission_id ?? '';
            this.initialSubmitFormState = submissionId ? {
              submissionId,
              name: this.solutionName,
              description: this.solutionDescription,
              attachmentKeys: Object.keys(mapped).sort(),
              attachmentS3ByFilename: s3ByFilename
            } : null;
          } else {
            this.initialSubmitFormState = null;
          }
        } else {
          this.initialSubmitFormState = null;
        }
      } catch {
        this.toasterService.error('Could not load previous submission', 3000);
        this.initialSubmitFormState = null;
      } finally {
        this.loadingPreviousSubmission = false;
        this.cdr.markForCheck();
      }
    } else {
      this.previousSubmissionAttachments = {};
      this.initialSubmitFormState = null;
    }
    this.showSubmitModal = true;
  }

  closeSubmitModal(): void {
    this.showSubmitModal = false;
    // Reset form on close
    this.resetSubmitForm();
  }

  resetSubmitForm(): void {
    this.solutionName = '';
    this.solutionDescription = '';
    this.solutionZipFile = null;
    this.solutionInstructionsFile = null;
    this.solutionZipObjectKey = null;
    this.solutionInstructionsObjectKey = null;
    this.submissionBatchId = null;
    this.previousSubmissionAttachments = {};
    this.initialSubmitFormState = null;
  }

  /** Whether we have existing attachments from a previous submission (resubmit flow). */
  get hasResubmissionAttachments(): boolean {
    return Object.keys(this.previousSubmissionAttachments).length > 0;
  }

  /** First ZIP file from previous submission (for left column). */
  get resubmissionZipFile(): { filename: string } | null {
    const entry = Object.entries(this.previousSubmissionAttachments).find(([name]) =>
      name.toLowerCase().endsWith('.zip')
    );
    return entry ? { filename: entry[0] } : null;
  }

  /** First PDF/DOC file from previous submission (for right column). */
  get resubmissionInstructionsFile(): { filename: string } | null {
    const entry = Object.entries(this.previousSubmissionAttachments).find(([name]) => {
      const lower = name.toLowerCase();
      return lower.endsWith('.pdf') || lower.endsWith('.doc') || lower.endsWith('.docx');
    });
    return entry ? { filename: entry[0] } : null;
  }

  /** Remove a file from the resubmission attachments list. */
  removePreviousSubmissionFile(filename: string): void {
    const next = { ...this.previousSubmissionAttachments };
    delete next[filename];
    this.previousSubmissionAttachments = next;
    this.cdr.markForCheck();
  }

  private ensureSubmissionBatchId(): string {
    if (!this.submissionBatchId) {
      this.submissionBatchId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
    }
    return this.submissionBatchId;
  }

  async onSolutionZipFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        this.solutionZipFile = file;
        await this.uploadChallengeAttachment(file, 'zip');
      } else {
        this.toasterService.error('Please upload a ZIP file for the solution', 3000);
        input.value = '';
      }
    }
  }

  async onInstructionsFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const validExtensions = ['.pdf', '.doc', '.docx'];
      const isValidType = validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (isValidType) {
        this.solutionInstructionsFile = file;
        await this.uploadChallengeAttachment(file, 'instructions');
      } else {
        this.toasterService.error('Please upload a PDF or DOC/DOCX file for instructions', 3000);
        input.value = '';
      }
    }
  }

  private async uploadChallengeAttachment(file: File, type: 'zip' | 'instructions'): Promise<void> {
    const batchId = this.ensureSubmissionBatchId();
    this.uploadingAttachment = true;
    this.cdr.markForCheck();
    try {
      const res = await this.challengeApiService.uploadChallengeAttachment({
        batch_id: batchId,
        file_name: file.name
      });
      const presignedUrl = res?.data?.presigned_url ?? res?.presigned_url;
      if (presignedUrl) {
        await firstValueFrom(
          this.http.put(presignedUrl, file, {
            headers: { 'Content-Type': 'application/octet-stream' }
          })
        );
      }
      // Upload response has meta: { batch_id, object_key } - use these for delete API
      const meta = res?.meta ?? res?.data?.meta;
      const objectKey =
        meta?.object_key ??
        res?.object_key ??
        res?.data?.object_key ??
        res?.key ??
        res?.data?.key ??
        res?.s3_key ??
        res?.data?.s3_key ??
        null;
      const responseBatchId = meta?.batch_id ?? res?.batch_id ?? res?.data?.batch_id ?? batchId;
      if (responseBatchId) {
        this.submissionBatchId = responseBatchId;
      }
      if (type === 'zip') {
        this.solutionZipObjectKey = objectKey;
      } else {
        this.solutionInstructionsObjectKey = objectKey;
      }
      this.toasterService.success(`"${file.name}" uploaded`, 3000, false);
    } catch {
      this.toasterService.error(`Failed to upload "${file.name}"`, 3000, false);
      if (type === 'zip') {
        this.solutionZipFile = null;
      } else {
        this.solutionInstructionsFile = null;
      }
    } finally {
      this.uploadingAttachment = false;
      this.cdr.markForCheck();
    }
  }

  async clearSolutionZipFile(): Promise<void> {
    const batchId = this.submissionBatchId ?? this.ensureSubmissionBatchId();
    const objectKey = this.solutionZipObjectKey;
    this.solutionZipFile = null;
    this.solutionZipObjectKey = null;
    const input = document.getElementById('solutionZipFile') as HTMLInputElement;
    if (input) input.value = '';
    if (objectKey && batchId) {
      this.uploadingAttachment = true;
      this.cdr.markForCheck();
      try {
        await this.challengeApiService.deleteChallengeAttachment({
          batch_id: batchId,
          object_key: objectKey
        });
        this.toasterService.success('Solution file removed', 3000, false);
      } catch {
        this.toasterService.error('Failed to remove file', 3000, false);
      } finally {
        this.uploadingAttachment = false;
        this.cdr.markForCheck();
      }
    }
  }

  async clearInstructionsFile(): Promise<void> {
    const batchId = this.submissionBatchId ?? this.ensureSubmissionBatchId();
    const objectKey = this.solutionInstructionsObjectKey;
    this.solutionInstructionsFile = null;
    this.solutionInstructionsObjectKey = null;
    const input = document.getElementById('instructionsFile') as HTMLInputElement;
    if (input) input.value = '';
    if (objectKey && batchId) {
      this.uploadingAttachment = true;
      this.cdr.markForCheck();
      try {
        await this.challengeApiService.deleteChallengeAttachment({
          batch_id: batchId,
          object_key: objectKey
        });
        this.toasterService.success('Instructions file removed', 3000, false);
      } catch {
        this.toasterService.error('Failed to remove file', 3000, false);
      } finally {
        this.uploadingAttachment = false;
        this.cdr.markForCheck();
      }
    }
  }

  async onSubmitSolutionConfirm(): Promise<void> {
    // Validate form
    if (!this.solutionName.trim()) {
      this.toasterService.error('Please enter a solution name', 3000);
      return;
    }

    if (!this.solutionDescription.trim()) {
      this.toasterService.error('Please enter a solution description', 3000);
      return;
    }

    const hasZip = !!this.resubmissionZipFile || !!(this.solutionZipFile && this.solutionZipObjectKey);
    const hasInstructions = !!this.resubmissionInstructionsFile || !!(this.solutionInstructionsFile && this.solutionInstructionsObjectKey);

    if (!this.challengeId) {
      this.toasterService.error('Challenge not found', 3000);
      return;
    }

    const isResubmission = !!this.initialSubmitFormState;

    if (isResubmission) {
      if (!hasZip) {
        this.toasterService.error('Please upload a solution ZIP file', 3000);
        return;
      }
      if (!hasInstructions) {
        this.toasterService.error('Please upload instruction file (PDF/DOC)', 3000);
        return;
      }

      const init = this.initialSubmitFormState!;
      if (!init.submissionId) {
        this.toasterService.error('Unable to resubmit: submission not found', 3000);
        return;
      }
      const add: string[] = [this.solutionZipObjectKey, this.solutionInstructionsObjectKey].filter(Boolean) as string[];
      const removedFilenames = init.attachmentKeys.filter((f) => !(f in this.previousSubmissionAttachments));
      const remove: string[] = removedFilenames
        .map((f) => init.attachmentS3ByFilename[f])
        .filter(Boolean);

      try {
        await this.challengeApiService.resubmitChallenge(init.submissionId, {
          title: this.solutionName.trim(),
          description: this.solutionDescription.trim(),
          attachments: { add, remove }
        });
        this.hasSubmittedSolution = true;
        this.toasterService.success('Solution submitted successfully', 3000);
        this.closeSubmitModal();
      } catch {
        this.toasterService.error('Failed to submit solution. Please try again.', 3000);
      }
    } else {
      if (!this.solutionZipFile) {
        this.toasterService.error('Please upload a solution ZIP file', 3000);
        return;
      }
      if (!this.solutionInstructionsFile) {
        this.toasterService.error('Please upload instruction file (PDF/DOC)', 3000);
        return;
      }
      if (!this.solutionZipObjectKey || !this.solutionInstructionsObjectKey) {
        this.toasterService.error('Please wait for file uploads to complete', 3000);
        return;
      }

      const attachments = [this.solutionZipObjectKey, this.solutionInstructionsObjectKey].filter(Boolean);

      try {
        await this.challengeApiService.submitChallenge(this.challengeId, {
          title: this.solutionName.trim(),
          description: this.solutionDescription.trim(),
          attachments
        });
        this.hasSubmittedSolution = true;
        this.toasterService.success('Solution submitted successfully', 3000);
        this.closeSubmitModal();
      } catch {
        this.toasterService.error('Failed to submit solution. Please try again.', 3000);
      }
    }
  }

  onSubmitSolutionCancel(): void {
    this.closeSubmitModal();
  }

  get isSubmitSolutionFormValid(): boolean {
    const hasNameAndDesc = !!(this.solutionName?.trim() && this.solutionDescription?.trim());
    if (this.hasResubmissionAttachments) {
      const hasZip = !!this.resubmissionZipFile || !!(this.solutionZipFile && this.solutionZipObjectKey);
      const hasInstructions = !!this.resubmissionInstructionsFile || !!(this.solutionInstructionsFile && this.solutionInstructionsObjectKey);
      return hasNameAndDesc && hasZip && hasInstructions;
    }
    return !!(hasNameAndDesc && this.solutionZipFile && this.solutionInstructionsFile);
  }

  /** True if form has changes from initial state (resubmission). For new submission, always true. */
  get hasSubmitFormChanges(): boolean {
    if (!this.initialSubmitFormState) {
      return true;
    }
    const init = this.initialSubmitFormState;
    const currentKeys = Object.keys(this.previousSubmissionAttachments).sort();
    const attachmentsChanged =
      currentKeys.length !== init.attachmentKeys.length ||
      currentKeys.some((k, i) => k !== init.attachmentKeys[i]);
    const hasNewUploads = !!(this.solutionZipObjectKey || this.solutionInstructionsObjectKey);
    return (
      this.solutionName.trim() !== init.name.trim() ||
      this.solutionDescription.trim() !== init.description.trim() ||
      attachmentsChanged ||
      hasNewUploads
    );
  }

  get isJoinCompetitionDisabled(): boolean {
    // Disable if evaluation has ended (evaluation end date < today)
    if (this.timelineDates.evaluationEnd) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const evaluationEnd = new Date(this.timelineDates.evaluationEnd);
      evaluationEnd.setHours(0, 0, 0, 0);
      if (evaluationEnd < today) return true;
    }
    // Disable if submission end date has passed
    if (!this.timelineDates.submissionEnd) {
      return false; // If no date set, allow joining
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const submissionEnd = new Date(this.timelineDates.submissionEnd);
    submissionEnd.setHours(0, 0, 0, 0);
    return submissionEnd < today;
  }

  /** True when evaluation end date is before today; button should show "Completed challenge". */
  get isEvaluationPast(): boolean {
    if (!this.timelineDates.evaluationEnd) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const evaluationEnd = new Date(this.timelineDates.evaluationEnd);
    evaluationEnd.setHours(0, 0, 0, 0);
    return evaluationEnd < today;
  }

  /** True when user has joined and submission_starts_at is in the future (Submit/Resubmit button should be disabled). */
  get isSubmitDisabledBecauseNotStarted(): boolean {
    if (!this.hasJoinedCompetition || !this.timelineDates.submissionStart) return false;
    return new Date(this.timelineDates.submissionStart) > new Date();
  }

  /** Formatted submission start date for user message when disabled. */
  get submissionStartsAtFormatted(): string {
    if (!this.timelineDates.submissionStart) return '';
    return this.formatService.formatDateForCard(this.timelineDates.submissionStart);
  }

         toggleSection(sectionId: string): void {
           // Don't allow expanding rules section
           if (sectionId === 'rules') {
             return;
           }

           if (this.expandedSections.has(sectionId)) {
             // Close the section if it's already open
             this.expandedSections.delete(sectionId);
           } else {
             // Close all other sections and open the clicked one
             this.expandedSections.clear();
             this.expandedSections.add(sectionId);
           }
         }

         async handleRulesDownload(): Promise<void> {
           if (this.challengeId) {
             try {
               const res = await this.challengeApiService.downloadChallengeRulesAndGuideline(this.challengeId);
               const url = this.getRulesDownloadUrlFromResponse(res);
               if (url) {
                 this.downloadPDF(url);
                 return;
               }
             } catch {
               // Fall through to static PDF if API fails
             }
           }
           this.downloadPDF(this.rulesPdfUrl);
         }

         async handleRulesPreview(): Promise<void> {
           this.loadingRulesPreview = true;
           this.clearRulesPreviewModalBlob();
           try {
             if (!this.challengeId) {
               this.toasterService.error('Could not load rules preview.', 3000);
               return;
             }
             const res = await this.challengeApiService.downloadChallengeRulesAndGuideline(this.challengeId);
             const s3Url = this.getRulesDownloadUrlFromResponse(res);
             if (!s3Url || typeof s3Url !== 'string' || s3Url.trim().length === 0) {
               this.toasterService.error('Could not load rules preview.', 3000);
               return;
             }
             this.loadingRulesPreview = false;
             this.rulesPreviewModalUrl = null;
             this.rulesPreviewPdfLoading = true;
             this.showRulesPreviewModal = true;
             this.cdr.markForCheck();

             const blob = await firstValueFrom(
               this.http.get(s3Url, { responseType: 'blob', headers: { Accept: 'application/pdf' } })
             );
             const blobUrl = URL.createObjectURL(blob);
             this.rulesPreviewModalBlobUrl = blobUrl;
             this.rulesPreviewModalUrl = blobUrl;
             this.rulesPreviewPdfLoading = false;
             this.cdr.markForCheck();
           } catch {
             this.rulesPreviewPdfLoading = false;
             this.cdr.markForCheck();
             this.toasterService.error('Could not load rules preview.', 3000);
           } finally {
             this.loadingRulesPreview = false;
           }
         }

         private clearRulesPreviewModalBlob(): void {
           if (this.rulesPreviewModalBlobUrl) {
             URL.revokeObjectURL(this.rulesPreviewModalBlobUrl);
             this.rulesPreviewModalBlobUrl = null;
           }
           this.rulesPreviewModalUrl = null;
         }

         /** Extract S3/download URL from API response (download_url or downloadUrl, from data or root). */
         private getRulesDownloadUrlFromResponse(res: any): string | null {
           if (!res) return null;
           const raw = res.download_url ?? res.downloadUrl ?? res.data?.download_url ?? res.data?.downloadUrl;
           return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
         }

         closeRulesPreviewModal(): void {
           this.showRulesPreviewModal = false;
           this.clearRulesPreviewModalBlob();
         }

         downloadPDF(url: string): void {
           // Create a temporary anchor element to trigger download
           const link = document.createElement('a');
           link.href = url;
           link.download = 'challenge-rules-and-guidelines.pdf';
           link.target = '_blank';
           document.body.appendChild(link);
           link.click();
           document.body.removeChild(link);
         }

         isSectionExpanded(sectionId: string): boolean {
           return this.expandedSections.has(sectionId);
         }

         formatStartDate(date: Date): string {
           return new Intl.DateTimeFormat('en-US', {
             year: 'numeric',
             month: 'long',
             day: 'numeric'
           }).format(date);
         }

         toggleSidebar(): void {
           this.isSidebarOpen = !this.isSidebarOpen;
         }

         closeSidebar(): void {
           this.isSidebarOpen = false;
         }
       }

